import type { GetApiUserManagementV1ApprovalRequestsParams } from '@/libs/api-user-mgt/generated/model';
import type { GetApiUserManagementV1AuditLogParams } from '@/libs/api-user-mgt/generated/model';
import type { GetApiUserManagementV1UsersParams } from '@/libs/api-user-mgt/generated/model';

import { sanitizeSieveTermValue } from '@/userManagement/domain/utils/sieveFilterValue';
import { USER_MANAGEMENT_DEFAULT_PAGE_SIZE } from '@/userManagement/domain/constants/pagination';
import type { AuditListParams, UserListParams } from '@/userManagement/domain/types/QueryContracts';
import type { SieveParams } from '@/userManagement/domain/types/QueryContracts';

function appendFilter(base: string | undefined, part: string): string {
  if (!part) return base ?? '';
  if (!base) return part;
  return `${base},${part}`;
}

/**
 * Maps domain list params to v3 sieve query params (`Page`, `PageSize`, `Filters`, `Sorts`).
 * Extra list filters (`roleIds`, `databases`, etc.) are appended to `Filters` using field names
 * aligned with `buildSieveParams` / DataGrid (`PermissionRoleId`, assigned DB names, etc.).
 */
export function toUserManagementUsersQueryParams(params: UserListParams): GetApiUserManagementV1UsersParams {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? USER_MANAGEMENT_DEFAULT_PAGE_SIZE;

  const parts: string[] = [];
  if (params.filters) parts.push(params.filters);
  const searchOperand = params.search?.trim() ? sanitizeSieveTermValue(params.search.trim()).trim() : '';
  if (searchOperand) {
    // TROC-111: Sieve 2.5.5 grouped-OR — `Contains` across any of the four identity-text columns.
    // The BE has these properties registered as filterable via SieveConfigurationForCrossUsers.
    parts.push(`(Username|FirstName|LastName|PrimaryEmail)@=${searchOperand}`);
  }
  // TROC-194: Role filter — supports specific role IDs, "Not Assigned" (null role), or both.
  // When both are selected, emit Sieve OR group covering null + specific IDs.
  const roleOperands =
    params.roleIds !== undefined && params.roleIds.length > 0
      ? params.roleIds.filter((id) => typeof id === 'number' && Number.isInteger(id) && id >= 0).map((id) => String(id))
      : [];

  if (params.roleNotAssigned === true) {
    if (roleOperands.length > 0) {
      // Union: users with specific roles OR null role
      // Sieve syntax: (PermissionRoleId==null|PermissionRoleId==1|2)
      parts.push(`(PermissionRoleId==null|PermissionRoleId==${roleOperands.join('|')})`);
    } else {
      parts.push('PermissionRoleId==null');
    }
  } else if (roleOperands.length > 0) {
    // TROC-109: BE `PermissionRoleId` is `int`. Sieve's `@=` (contains) silently returns all
    // rows on numeric columns; only `==` filters int equality. `|` between values is OR.
    parts.push(`PermissionRoleId==${roleOperands.join('|')}`);
  }
  if (params.databases !== undefined && params.databases.length > 0) {
    const dbOperands = params.databases.map((d) => sanitizeSieveTermValue(d.trim()).trim()).filter((d) => d.length > 0);
    if (dbOperands.length > 0) {
      parts.push(`AssignedDatabase@=${dbOperands.join('|')}`);
    }
  }

  // TROC-199: default to active users only; "show deactivated" toggle lists inactive users.
  // Snapshot delta wide-fetch sets omitActivityFilter so inactive changed users are not dropped.
  if (params.omitActivityFilter === true) {
    // no IsActive clause
  } else if (params.showDeactivated === true) {
    parts.push('IsActive==false');
  } else {
    parts.push('IsActive==true');
  }

  // Snapshot delta path: BE binds via top-level query params, not Sieve.
  // an active snapshot baseline + a numeric snapshot id and toggles "Changes vs snapshot",
  // we promote the toggle to `permissionSnapshotId` + `snapshotChangesOnly=true`.
  // Without a baseline the "Changes only" toggle is disabled in the UI (TROC-109) — there is
  // no meaningful Sieve fallback because there's no reference point to diff against.
  const snapshotId = params.permissionSnapshotId;
  const useSnapshotDelta =
    params.snapshotChangesOnly === true && typeof snapshotId === 'number' && Number.isInteger(snapshotId) && snapshotId > 0;

  const Filters = parts.length > 0 ? parts.join(',') : undefined;

  const out: GetApiUserManagementV1UsersParams = {
    Page: page,
    PageSize: pageSize,
    Sorts: params.sorts,
    Filters
  };

  if (useSnapshotDelta) {
    out.permissionSnapshotId = snapshotId;
    out.snapshotChangesOnly = true;
  }

  // TROC-35: BE binds this via `[FromQuery] bool? hasRoleDeviation` (camelCase, no Sieve).
  // Travels as a sibling of Filters on the request, not embedded inside the Sieve filter string.
  // Conditional assignment (instead of explicit `undefined`) mirrors the snapshot-delta pattern above.
  if (params.hasRoleDeviation === true) {
    out.hasRoleDeviation = true;
  }

  return out;
}

/** Maps to flat SieveModel query keys (`filters`, `sorts`, `page`, `pageSize`) for `GET /audit-log`. */
export function toUserManagementAuditQueryParams(params: AuditListParams): GetApiUserManagementV1AuditLogParams {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? USER_MANAGEMENT_DEFAULT_PAGE_SIZE;
  let filters = params.filters;
  const auditSearch = params.search?.trim() ? sanitizeSieveTermValue(params.search.trim()).trim() : '';
  if (auditSearch) {
    filters = appendFilter(filters, `action@=${auditSearch}`);
  }
  if (params.actionType?.trim()) {
    const v = sanitizeSieveTermValue(params.actionType.trim()).trim();
    if (v) {
      filters = appendFilter(filters, `action==${v}`);
    }
  }
  if (params.targetType?.trim()) {
    const v = sanitizeSieveTermValue(params.targetType.trim()).trim();
    if (v) {
      filters = appendFilter(filters, `targetType==${v}`);
    }
  }
  // TROC-286: Sieve clause (not top-level Users-style param) — BE filters audit rows by HasRoleDeviation.
  if (params.hasRoleDeviation === true) {
    filters = appendFilter(filters, 'hasRoleDeviation==true');
  }

  const auditOut: GetApiUserManagementV1AuditLogParams & { defaultView?: boolean } = {
    page,
    pageSize,
    sorts: params.sorts,
    filters: filters || undefined,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo
  };

  if (params.defaultView === true) {
    auditOut.defaultView = true;
  }

  return auditOut;
}

/**
 * Maps tab status to Sieve `Filters` so the User Management API applies status on the server.
 * `PendingApproval` = union of in-flight buckets (review queue + IT queue), aligned with summary cards.
 */
export function approvalStatusToSieveFilterFragment(status: string | undefined): string | null {
  if (status == null || status === '') {
    return null;
  }
  if (status === 'PendingApproval') {
    return 'Status@=PendingReview|PendingIT';
  }
  if (status === 'Approved' || status === 'Rejected' || status === 'Cancelled' || status === 'PendingReview' || status === 'PendingIT') {
    return `Status==${status}`;
  }
  return null;
}

export function toApprovalRequestsQueryParams(params: SieveParams): GetApiUserManagementV1ApprovalRequestsParams {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? USER_MANAGEMENT_DEFAULT_PAGE_SIZE;
  let Filters = params.filters;
  const approvalSearch = params.search?.trim() ? sanitizeSieveTermValue(params.search.trim()).trim() : '';
  if (approvalSearch) {
    Filters = appendFilter(Filters, `TargetUsername@=${approvalSearch}`);
  }
  const statusPart = approvalStatusToSieveFilterFragment(params.status);
  if (statusPart) {
    Filters = appendFilter(Filters, statusPart);
  }

  const approvalsOut: GetApiUserManagementV1ApprovalRequestsParams & { defaultView?: boolean } = {
    Page: page,
    PageSize: pageSize,
    Sorts: params.sorts,
    Filters: Filters || undefined
  };

  if (params.defaultView === true) {
    approvalsOut.defaultView = true;
  }

  return approvalsOut;
}
